import { MonetaryAmount } from '@components'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  AccountCode,
  Caret,
  DateCell,
  ReportEmptyMessage,
  ReportRow,
  RowLabel,
  RowLabelText,
  ValueCell
} from './interactive.styled'
import { ReportLeadingGutter } from './ReportLeadingGutter'

const VALUE_WIDTH = '120px'

type Totals = {
  debet?: number | null
  credit?: number | null
}

type EmptyMessageProps = {
  hasTagFilter?: boolean
}

const useEmptyMessage = ({ hasTagFilter = false }: EmptyMessageProps) => {
  const [t] = useTranslation()
  return hasTagFilter
    ? t('reports.interactive.noEntriesForTags')
    : t('reports.interactive.noEntries')
}

export const LedgerListEmptyState: React.FC<{
  totals?: Totals | null
  currency?: string
  hasTagFilter?: boolean
}> = ({ totals, currency, hasTagFilter }) => {
  const [t] = useTranslation()
  const message = useEmptyMessage({ hasTagFilter })
  const debit = totals?.debet ?? 0
  const credit = totals?.credit ?? 0

  return (
    <>
      <ReportRow>
        <ReportLeadingGutter />
        <RowLabel>
          <ReportEmptyMessage>{message}</ReportEmptyMessage>
        </RowLabel>
        <ValueCell $width={VALUE_WIDTH} />
        <ValueCell $width={VALUE_WIDTH} />
        <ValueCell $width={VALUE_WIDTH} />
        <ValueCell />
      </ReportRow>
      <ReportRow $strong $bg="#f7fafd" $borderStrong>
        <AccountCode aria-hidden />
        <RowLabel $strong>
          <RowLabelText>{t('reports.interactive.reportTotal')}</RowLabelText>
        </RowLabel>
        <ValueCell $width={VALUE_WIDTH} />
        <ValueCell $width={VALUE_WIDTH} $strong>
          <MonetaryAmount value={debit} currency={currency} />
        </ValueCell>
        <ValueCell $width={VALUE_WIDTH} $strong>
          <MonetaryAmount value={credit} currency={currency} />
        </ValueCell>
        <ValueCell $strong />
      </ReportRow>
    </>
  )
}

export const JournalListEmptyState: React.FC<{
  totals?: Totals | null
  currency?: string
  hasTagFilter?: boolean
  statusIconWidth: string
}> = ({ totals, currency, hasTagFilter, statusIconWidth }) => {
  const [t] = useTranslation()
  const message = useEmptyMessage({ hasTagFilter })
  const debit = totals?.debet ?? 0
  const credit = totals?.credit ?? 0

  return (
    <>
      <ReportRow>
        <Caret aria-hidden />
        <DateCell />
        <RowLabel>
          <ReportEmptyMessage>{message}</ReportEmptyMessage>
        </RowLabel>
        <ValueCell $width={VALUE_WIDTH} />
        <ValueCell $width={VALUE_WIDTH} />
        <ValueCell $width={statusIconWidth} />
      </ReportRow>
      <ReportRow $strong $bg="#f7fafd" $borderStrong>
        <Caret aria-hidden />
        <DateCell aria-hidden />
        <RowLabel $strong>
          <RowLabelText>{t('reports.interactive.reportTotal')}</RowLabelText>
        </RowLabel>
        <ValueCell $width={VALUE_WIDTH} $strong>
          <MonetaryAmount value={debit} currency={currency} />
        </ValueCell>
        <ValueCell $width={VALUE_WIDTH} $strong>
          <MonetaryAmount value={credit} currency={currency} />
        </ValueCell>
        <ValueCell $width={statusIconWidth} />
      </ReportRow>
    </>
  )
}
